import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Image,
  Tooltip,
  Spinner,
} from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';

import { useAssociationsButtonStyles, useImagePreviewStyles, useValueWithTooltipViewStyles } from '../../styles/summary-result-dialog-style';

const ValueWithTooltip = ({
  value, valueDescription, ellipsisOnLeft,
}) => {
  const classes = useValueWithTooltipViewStyles();

  if (valueDescription) {
    return (
      <Tooltip
        content={valueDescription}
        relationship="description"
        withArrow
      >
        <div className={ellipsisOnLeft ? classes.overflowLeft : classes.overflow}>{value}</div>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      content={value}
      relationship="description"
      withArrow
      positioning="above-start"
    >
      <div className={ellipsisOnLeft ? classes.overflowLeft : classes.overflow}>{value}</div>
    </Tooltip>
  );
};

ValueWithTooltip.propTypes = {
  value: PropTypes.string,
  valueDescription: PropTypes.string,
  ellipsisOnLeft: PropTypes.bool,
};

ValueWithTooltip.defaultProps = {
  value: '',
  valueDescription: '',
  ellipsisOnLeft: false,
};

const Associations = ({
  assetId, folderAssetType, availableActions, i18n, navigation, logger,
}) => {
  const classes = useAssociationsButtonStyles();

  const [isLoading, setLoading] = useState(false);
  const onClick = useCallback(() => {
    logger.logTrace('View all ads using this asset clicked');
    setLoading(true);
    return navigation.gotoAdsPageWithAssetIdFilter({ folderAssetType, assetId }, true)
      .finally(() => setLoading(false));
  }, [assetId, folderAssetType, logger, navigation]);

  return (
    <div>
      {availableActions.gotoAds ? (
        <Button
          icon={<OpenRegular />}
          onClick={onClick}
          disabled={isLoading}
          className={classes.associationsButton}
        >
          {i18n.getString(_TL_('View all ads using this asset'))}
        </Button>
      ) : (i18n.getString(_TL_('No associations')))}
      {isLoading && <Spinner size="extra-tiny" />}
    </div>
  );
};

Associations.propTypes = {
  assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  folderAssetType: PropTypes.string,
  availableActions: PropTypes.shape({
    gotoAds: PropTypes.bool,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  navigation: PropTypes.shape({
    gotoAdsPageWithAssetIdFilter: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};

Associations.defaultProps = {
  folderAssetType: '',
};

const ImagePreview = ({
  id, name, folderAssetType, thumbnailUrl, onRenderPreview,
}) => {
  const classes = useImagePreviewStyles();
  let preview = '';
  if (onRenderPreview) {
    preview = onRenderPreview();
  } else if (thumbnailUrl) {
    preview = (<Image
      src={thumbnailUrl}
      alt={`${folderAssetType || ''} ${name || id || ''}`}
      fit="cover"
      width={92}
      height={68}
    />);
  }

  return (
    <div className={classes.imagePreviewContainer}>
      {preview}
    </div>
  );
};

ImagePreview.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  folderAssetType: PropTypes.string,
  onRenderPreview: PropTypes.func,
  thumbnailUrl: PropTypes.string,
};

ImagePreview.defaultProps = {
  id: '',
  name: '',
  folderAssetType: '',
  onRenderPreview: undefined,
  thumbnailUrl: undefined,
};

const headerWithPopup = (popupId, item, helpPopup) => (
  <>
    <span className="name">{item.name}</span>
    {helpPopup(popupId)}
  </>
);

export const colPreview = (i18n, helpPopup) => ({
  columnId: 'colPreview',
  name: i18n.getString(_TL_('Preview')),
  onRender: item => <ImagePreview {...item} />,
  onRenderHeader: item => headerWithPopup('pop_BA_AssetLibrary_Preview', item, helpPopup),
  compare: (a, b) => a.thumbnailUrl.localeCompare(b.thumbnailUrl),
});

export const colId = (i18n, helpPopup) => ({
  columnId: 'colId',
  name: i18n.getString(_TL_('ID')),
  onRender: item => item.id,
  onRenderHeader: item => headerWithPopup('pop_BA_AssetLibrary_ID', item, helpPopup),
  compare: (a, b) => a.id.localeCompare(b.id),
});

export const colName = (i18n, helpPopup) => ({
  columnId: 'colName',
  name: i18n.getString(_TL_('Name')),
  onRender: item => <ValueWithTooltip value={item.name} />,
  onRenderHeader: item => headerWithPopup('pop_BA_AssetLibrary_Name', item, helpPopup),
  compare: (a, b) => a.name.localeCompare(b.name),
});

export const colPath = (i18n, helpPopup) => ({
  columnId: 'colPath',
  name: i18n.getString(_TL_('Location')),
  onRender: item => <ValueWithTooltip value={item.path} ellipsisOnLeft />,
  onRenderHeader: item => headerWithPopup('pop_BA_AssetLibrary_Location', item, helpPopup),
  compare: (a, b) => a.path.localeCompare(b.path),
});

export const colType = (i18n, helpPopup) => ({
  columnId: 'colType',
  name: i18n.getString(_TL_('Type')),
  onRender: item => item.type,
  onRenderHeader: item => headerWithPopup('pop_BA_AssetLibrary_AssetType', item, helpPopup),
  compare: (a, b) => a.type.localeCompare(b.type),
});

export const colError = (i18n, popupId, helpPopup) => ({
  columnId: 'colError',
  name: i18n.getString(_TL_('Errors')),
  onRender: item => <ValueWithTooltip value={item.shortErrMsg} valueDescription={item.errorMessage} />,
  onRenderHeader: item => headerWithPopup(popupId, item, helpPopup),
  compare: (a, b) => a.shortErrMsg.localeCompare(b.shortErrMsg),
});
export const colDeletionError = (i18n, helpPopup) => colError(i18n, 'pop_BA_AssetLibrary_DeletionError', helpPopup);
export const colMoveError = (i18n, helpPopup) => colError(i18n, 'pop_BA_AssetLibrary_MoveError', helpPopup);

export const colAssociations = (i18n, navigation, logger, helpPopup) => ({
  columnId: 'colAssociations',
  name: i18n.getString(_TL_('Associations')),
  onRender: ({
    id, folderAssetType, availableActions,
  }) => (
    <Associations
      assetId={id}
      folderAssetType={folderAssetType}
      availableActions={availableActions}
      i18n={i18n}
      navigation={navigation}
      logger={logger}
    />
  ),
  onRenderHeader: item => headerWithPopup('pop_BA_AssetLibrary_Associations', item, helpPopup),
  compare: (a, b) => a.availableActions.localeCompare(b.availableActions),
});

export const columnSizingOptions = {
  colPreview: { minWidth: 100 },
  colId: { minWidth: 110, maxWidth: 110 },
  colName: { minWidth: 100 },
  colPath: { minWidth: 100 },
  colType: { minWidth: 80 },
  colError: { minWidth: 170 },
  colAssociations: { minWidth: 240 },
};
